import React, { useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@material-ui/core';
import i18n from 'i18n-js';

import { ReservationDTO, Reservations, SeatedEditConflict } from 'api/controllers/reservations';
import {
    formValuesToModel,
    getDatePart,
    ReservationFormValues,
    reservationToFormValues,
    validateEditReservationForm,
    validateSeatedReservationForm,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import ReservationEditor from './components/ReservationEditor';
import { RESERVATIONS_PATH, useReservationsSubpageGuard, useThunkDispatch } from './hooks';
import { ACTIVE_TAB_STATE } from './index';

export default function EditReservationPage() {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const dispatch = useThunkDispatch();
    const isModuleDisabled = useReservationsSubpageGuard();
    const restaurantId = useAppSelector((state) => state.user.currentRestaurantId);
    const [reservation, setReservation] = useState<ReservationDTO | null>(null);

    const showDetails = (current: ReservationDTO) =>
        history.replace(RESERVATIONS_PATH, {
            ...ACTIVE_TAB_STATE,
            date: getDatePart(current.reservationStartDateTime),
            reservation: current,
        });
    const backToActive = () =>
        reservation
            ? showDetails(reservation)
            : history.replace(RESERVATIONS_PATH, ACTIVE_TAB_STATE);

    useEffect(() => {
        let active = true;
        setReservation(null);
        if (!isModuleDisabled) {
            Reservations.getById(id)
                .then((current) => {
                    if (!active) return;
                    if (!['Confirmed', 'Seated'].includes(current.status) || current.isArchived) {
                        showDetails(current);
                    } else {
                        setReservation(current);
                    }
                })
                .catch((error) => {
                    if (active) {
                        dispatch(globalStore.actionCreators.showToaster('error', error));
                        backToActive();
                    }
                });
        }
        return () => {
            active = false;
        };
    }, [id, restaurantId, isModuleDisabled, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async (
        values: ReservationFormValues
    ): Promise<string | SeatedEditConflict | null> => {
        try {
            const update =
                reservation?.status === 'Seated'
                    ? reservationsStore.actionCreators.updateSeated
                    : reservationsStore.actionCreators.updateConfirmed;
            const model = formValuesToModel(values);
            if (
                reservation?.status === 'Seated' &&
                values.date === getDatePart(reservation.reservationStartDateTime) &&
                values.time === reservation.reservationStartDateTime.slice(11, 16)
            ) {
                model.reservationStartDateTime = reservation.reservationStartDateTime;
            }
            const result = await dispatch(
                update(id, {
                    ...model,
                    expectedVersion: reservation?.version ?? 0,
                })
            );
            if (result.status === 'tableTaken') return result.tableId;
            if (
                result.status === 'removedTableOrderOpen' ||
                result.status === 'retainedTableTaken'
            ) {
                return result;
            }
            if (result.status === 'alreadyHandled') {
                const current = await Reservations.getById(id);
                dispatch(reservationsStore.actionCreators.applyRealtimeUpdate(current, true));
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('reservations.changedOnAnotherDevice')
                    )
                );
                showDetails(current);
                return null;
            }
            dispatch(
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t(
                        result.updatedEmailSent
                            ? 'reservations.updatedWithEmail'
                            : 'reservations.updated'
                    )
                )
            );
            if (result.updatedEmailFailed) {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('reservations.updateEmailFailed')
                    )
                );
            }
            showDetails(result.reservation);
        } catch (error) {
            dispatch(
                globalStore.actionCreators.showToaster('error', i18n.t('reservations.couldNotSave'))
            );
        }
        return null;
    };

    if (isModuleDisabled) return <Redirect to={RESERVATIONS_PATH} />;
    if (!reservation) {
        return (
            <Box p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <ReservationEditor
            key={reservation.id}
            title={i18n.t('reservations.editReservation').toUpperCase()}
            channelLine={null}
            reservationId={reservation.id}
            status={reservation.status}
            initialValues={reservationToFormValues(reservation)}
            validate={
                reservation.status === 'Seated'
                    ? (values, now) =>
                          validateSeatedReservationForm(
                              values,
                              now,
                              reservationToFormValues(reservation)
                          )
                    : validateEditReservationForm
            }
            isEditing
            confirmLabel={i18n.t('reservations.saveChanges')}
            onCancel={backToActive}
            onConfirm={handleSave}
        />
    );
}
